/**
 * Bike stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bikeStickersPack: ToilePack = {
    slug: 'bike-stickers',
    category: 'sticker',
    name: 'Bike stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'road', label: 'Road bike', subject: 'a road bike in profile with drop bars'},
        {key: 'wheel', label: 'Wheel', subject: 'a bicycle wheel with spokes'},
        {key: 'badge', label: 'Ride badge', subject: 'a round badge lettered "RIDE" with a chainring'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a mountain bike instead', 'a whole sticker sheet', 'in neon colors'],
    directionHint: 'try: a mountain bike instead',
    seo: {
        title: 'Bike Stickers: Road Bike, Wheel, Ride Badge',
        metaDescription: 'Nine bike stickers — a road bike in profile, a bicycle wheel, a lettered ride badge — in retro, bold cartoon, and minimal line styles.',
        h1: 'Bike stickers: road bike, wheel, ride badge',
        tagline: 'Nine stickers, three cycling icons, three styles each.',
        intro: "A road bike in profile with drop bars, a bicycle wheel with spokes, and a round badge lettered 'RIDE' with a chainring — three cycling stickers, die-cut and ready to print. Each rendered retro, bold cartoon, and minimal single-line. Ask for a mountain bike instead, a trail scene, or a helmet composition, and the same three styles carry it.",
        useCases: [
            'a bike frame or helmet',
            'a laptop or water bottle',
            'a phone case sticker sheet',
            'a gift for a cyclist',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine bike stickers — a road bike, a bicycle wheel, and a lettered ride badge — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a mountain bike or a different scene?', a: 'Yes — the direction field takes any variation, including a mountain bike instead, a trail scene, or a helmet composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a frame-sized print.'},
            {q: 'Does the badge carry a real brand name?', a: "No — the lettering is generic ('RIDE'), not a real cycling brand or logo."},
        ],
    },
}
